"use client";

import { useState, useEffect, useRef } from "react";

const USER_ID = "cmn8mvfuk0000a0nf9bv7cj5c";

type Problem = {
  problemId: string;
  problemText: string;
  groupId: string;
  type: string;
  pCorrect: number;
};

type FocusStatus = {
  newCount: number;
  focusCount: number;
  oldCount: number;
  focusCorrectCount: number;
  masteryPercent: number;
  mastered: boolean;
};

type NextResponse = {
  problem: Problem | null;
  done: boolean;
  mastered: boolean;
  message?: string;
  focusStatus: FocusStatus;
};

type AnswerResult = {
  wasCorrect: boolean;
  correctAnswer: string;
  answerDescription: string;
  focusStatus: FocusStatus;
  justMastered: boolean;
  newFocusCount: number;
};

type Stats = {
  totalProblems: number;
  newCount: number;
  focusCount: number;
  focusCorrectCount: number;
  oldCount: number;
  masteryPercent: number;
  todayStats: { answered: number; correct: number; accuracy: number };
};

export default function Home() {
  const [inSession, setInSession] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [sessionStats, setSessionStats] = useState({ correct: 0, total: 0 });
  const [stats, setStats] = useState<Stats | null>(null);
  const [startTime, setStartTime] = useState<number>(0);

  // Focus Set state
  const [focusStatus, setFocusStatus] = useState<FocusStatus | null>(null);
  const [showMasteryModal, setShowMasteryModal] = useState(false);

  // Track recent wrong answers for retry
  const [recentWrong, setRecentWrong] = useState<string[]>([]);
  const [recentShown, setRecentShown] = useState<string[]>([]);
  const [consecutiveWrong, setConsecutiveWrong] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (problem && !result) {
      setStartTime(Date.now());
      inputRef.current?.focus();
    }
  }, [problem, result]);

  async function fetchStats() {
    const res = await fetch(`/api/stats?userId=${USER_ID}`);
    if (res.ok) {
      const data = await res.json();
      setStats(data);
      setFocusStatus({
        newCount: data.newCount,
        focusCount: data.focusCount,
        oldCount: data.oldCount,
        focusCorrectCount: data.focusCorrectCount,
        masteryPercent: data.masteryPercent,
        mastered: false,
      });
    }
  }

  async function startSession() {
    const res = await fetch("/api/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: USER_ID }),
    });

    if (res.ok) {
      const data = await res.json();
      setSessionId(data.sessionId);
      setInSession(true);
      setSessionStats({ correct: 0, total: 0 });
      setRecentWrong([]);
      setRecentShown([]);
      setConsecutiveWrong(0);
      setFocusStatus({
        newCount: data.newCount,
        focusCount: data.focusCount,
        oldCount: data.oldCount,
        focusCorrectCount: data.focusCorrectCount,
        masteryPercent: data.masteryPercent,
        mastered: false,
      });
      await fetchNextProblem([], [], 0);
    }
  }

  async function fetchNextProblem(wrong: string[], shown: string[], consWrong: number) {
    setLoading(true);
    setResult(null);
    setAnswer("");

    const res = await fetch("/api/next", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId: USER_ID,
        sessionId,
        recentWrong: wrong,
        recentShown: shown,
        consecutiveWrong: consWrong,
      }),
    });

    if (res.ok) {
      const data: NextResponse = await res.json();
      setFocusStatus(data.focusStatus);

      if (data.mastered) {
        setShowMasteryModal(true);
        setProblem(null);
      } else if (data.done) {
        endSession();
      } else if (data.problem) {
        setProblem(data.problem);
        setRecentShown(prev => [...prev.slice(-9), data.problem!.problemId]);
      }
    }
    setLoading(false);
  }

  async function submitAnswer() {
    if (!problem || !answer.trim() || !sessionId) return;

    setLoading(true);
    const responseMs = Date.now() - startTime;

    const res = await fetch("/api/answer", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId: USER_ID,
        problemId: problem.problemId,
        sessionId,
        userInput: answer.trim(),
        responseMs,
        presentedType: problem.type,
      }),
    });

    if (res.ok) {
      const data: AnswerResult = await res.json();
      setResult(data);
      setFocusStatus(data.focusStatus);

      // Update stats
      setSessionStats(prev => ({
        correct: prev.correct + (data.wasCorrect ? 1 : 0),
        total: prev.total + 1,
      }));

      // Track wrong answers for retry
      if (!data.wasCorrect) {
        setRecentWrong(prev => {
          const updated = [...prev.filter(id => id !== problem.problemId), problem.problemId];
          return updated.slice(-5);
        });
        setConsecutiveWrong(prev => prev + 1);
      } else {
        setRecentWrong(prev => prev.filter(id => id !== problem.problemId));
        setConsecutiveWrong(0);
      }

      // Check if mastery was just achieved
      if (data.justMastered) {
        setShowMasteryModal(true);
      }
    }
    setLoading(false);
  }

  async function nextProblem() {
    await fetchNextProblem(recentWrong, recentShown, consecutiveWrong);
  }

  async function handlePromoteFocusSet() {
    setLoading(true);
    const res = await fetch("/api/promote", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: USER_ID }),
    });

    if (res.ok) {
      const data = await res.json();
      setFocusStatus({
        newCount: data.newCount,
        focusCount: data.focusCount,
        oldCount: data.oldCount,
        focusCorrectCount: data.focusCorrectCount,
        masteryPercent: data.masteryPercent,
        mastered: false,
      });
      setShowMasteryModal(false);
      setRecentWrong([]);
      setRecentShown([]);
      setConsecutiveWrong(0);
      await fetchNextProblem([], [], 0);
    }
    setLoading(false);
  }

  function endSession() {
    setInSession(false);
    setProblem(null);
    setResult(null);
    setSessionId(null);
    fetchStats();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      if (result) {
        nextProblem();
      } else if (answer.trim()) {
        submitAnswer();
      }
    }
  }

  const accuracy = sessionStats.total > 0
    ? Math.round((sessionStats.correct / sessionStats.total) * 100)
    : 0;

  return (
    <main className="min-h-screen bg-gray-900 text-white p-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-center">年号クイズ</h1>

        {/* Stats bar */}
        {stats && (
          <div className="bg-gray-800 rounded p-3 mb-4 text-sm">
            <div className="grid grid-cols-4 gap-2 text-center mb-3">
              <div>
                <div className="text-gray-400">New</div>
                <div className="font-bold">{stats.newCount}</div>
              </div>
              <div>
                <div className="text-gray-400">Focus</div>
                <div className="font-bold text-yellow-400">{stats.focusCount}</div>
              </div>
              <div>
                <div className="text-gray-400">Old</div>
                <div className="font-bold text-green-400">{stats.oldCount}</div>
              </div>
              <div>
                <div className="text-gray-400">Today</div>
                <div className="font-bold">{stats.todayStats.answered}</div>
              </div>
            </div>

            {/* Focus Set Progress Bar */}
            <div className="mt-2">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Focus Set Mastery</span>
                <span>{stats.focusCorrectCount}/{stats.focusCount} ({stats.masteryPercent}%)</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    stats.masteryPercent >= 90 ? "bg-green-500" : "bg-yellow-500"
                  }`}
                  style={{ width: `${stats.masteryPercent}%` }}
                />
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Need 90% to advance
              </div>
            </div>
          </div>
        )}

        {/* Mastery Celebration Modal */}
        {showMasteryModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md text-center">
              <div className="text-5xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold mb-2">Focus Set Mastered!</h2>
              <p className="text-gray-300 mb-4">
                You've mastered 90% of your current Focus set!
                Ready to move on to new problems?
              </p>
              <div className="text-sm text-gray-400 mb-6">
                {focusStatus && (
                  <>
                    <div>{focusStatus.focusCount} problems will move to Old</div>
                    <div>{focusStatus.newCount > 0 ? `${Math.min(20, focusStatus.newCount)} new problems will be loaded` : "No new problems remaining"}</div>
                  </>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowMasteryModal(false);
                    endSession();
                  }}
                  className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  End Session
                </button>
                <button
                  onClick={handlePromoteFocusSet}
                  disabled={loading || (focusStatus?.newCount === 0)}
                  className="flex-1 py-2 bg-green-600 hover:bg-green-500 rounded font-semibold disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Continue"}
                </button>
              </div>
            </div>
          </div>
        )}

        {!inSession ? (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Start Session</h2>

            <p className="text-gray-400 text-sm mb-6">
              Practice your Focus set of 20 problems. Answer 90% correctly to unlock the next set.
            </p>

            <button
              onClick={startSession}
              disabled={loading}
              className="w-full py-3 bg-green-600 hover:bg-green-500 rounded-lg font-semibold disabled:opacity-50"
            >
              {loading ? "Loading..." : "Start"}
            </button>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg p-6">
            {/* Session Focus Progress */}
            {focusStatus && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Progress</span>
                  <span>{focusStatus.focusCorrectCount}/{focusStatus.focusCount} correct ({focusStatus.masteryPercent}%)</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      focusStatus.masteryPercent >= 90 ? "bg-green-500" : "bg-yellow-500"
                    }`}
                    style={{ width: `${focusStatus.masteryPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* Session stats */}
            <div className="flex justify-between text-sm text-gray-400 mb-4">
              <span>
                {problem?.type === "retry" && <span className="text-orange-400 mr-2">↩ Retry</span>}
                {problem?.type === "easy" && <span className="text-green-400 mr-2">✓ Easy</span>}
                {problem?.type === "hard" && <span className="text-red-400 mr-2">! Hard</span>}
                {problem?.type === "random" && <span className="text-blue-400 mr-2">? Random</span>}
              </span>
              <span>{sessionStats.correct}/{sessionStats.total} ({accuracy}%)</span>
            </div>

            {/* Wrong queue indicator */}
            {recentWrong.length > 0 && (
              <div className="text-xs text-orange-400 mb-4">
                {recentWrong.length} item{recentWrong.length > 1 ? "s" : ""} to retry
              </div>
            )}

            {problem && (
              <>
                <div className="text-xl mb-6 text-center py-4">
                  {problem.problemText}
                </div>

                <div className="mb-4">
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={!!result || loading}
                    placeholder="年を入力..."
                    className="w-full p-4 text-2xl text-center bg-gray-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    autoComplete="off"
                  />
                </div>

                {result && (
                  <div className={`p-4 rounded-lg mb-4 ${
                    result.wasCorrect ? "bg-green-900/50 border border-green-700" : "bg-red-900/50 border border-red-700"
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{result.wasCorrect ? "✓" : "✗"}</span>
                      <span className="text-xl font-bold">
                        {result.wasCorrect ? "正解!" : `不正解 → ${result.correctAnswer}`}
                      </span>
                    </div>
                    {result.answerDescription && (
                      <div className="text-gray-300 mt-2 text-sm">
                        {result.answerDescription}
                      </div>
                    )}
                  </div>
                )}

                {!result ? (
                  <button
                    onClick={submitAnswer}
                    disabled={loading || !answer.trim()}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-500 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {loading ? "..." : "Answer (Enter)"}
                  </button>
                ) : (
                  <button
                    onClick={nextProblem}
                    disabled={loading}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-500 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {loading ? "..." : "Next → (Enter)"}
                  </button>
                )}
              </>
            )}

            {loading && !problem && (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            )}
          </div>
        )}

        {inSession && (
          <button
            onClick={endSession}
            className="mt-4 w-full py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm"
          >
            End Session
          </button>
        )}
      </div>
    </main>
  );
}
